export const AUTH_401_WHITELIST_ROUTE_NAMES = [
  'Home',
  'Sem',
  'TransferPage',
  // /:lang/company/
  'companyListBase',
  'companyListPage',
  'companyListCountry',
  'companyListCountryPage',
  // /:lang/membership/tools/risk/blackList
  'membershipToolsBlackList',
  // /:lang/membership/tools/riskPrevention/case
  'riskPreventionRiskCase',
  // /:lang/membership/tools/assurance/process
  'assuranceIntroductionProcess',
  // /:lang/JCInsurance/
  'JCInsurance',
  // /:lang/marketing/credit
  'Credit',
  // /:lang/finance-jcpay/
  'JCPayPay',
  // /:lang/finance-usd/
  'JCPayUsd',
  // /:lang/vip/（含 #general / #specialty / #vendor）
  'membershipJCTransIntroduction',
  // /:lang/JCVerified/
  'membershipJCVF',
  // /:lang/client/
  'client',
  // /:lang/jctransnews/
  'jctransNews',
  // /:lang/news/
  'logisticsNews',
  // /:lang/aboutus/
  'AboutUs',
] as const

/** 白名单内：401 只清登录态、不做 guest 重试（有登录限额等业务） */
export const AUTH_401_CLEAR_ONLY_ROUTE_NAMES = [
  'membershipToolsBlackList',
] as const

/**
 * path 兜底：中间件阶段 useRoute()/pendingNav 可能尚未切到目标页，
 * 用 path 判断避免首屏过期 token 误跳登录。
 */
export const AUTH_401_WHITELIST_PATH_PATTERNS = [
  /^\/(cn|en)\/?$/, // Home
  /^\/(cn|en)\/company(\/|$)/,
  /^\/(cn|en)\/company-page-\d+\/?$/,
  /^\/(cn|en)\/company-[^/]+\/?$/,
  /^\/(cn|en)\/membership\/tools\/risk\/blackList\/?$/,
  /^\/(cn|en)\/membership\/tools\/riskPrevention\/case\/?$/,
  /^\/(cn|en)\/membership\/tools\/assurance\/process\/?$/,
  /^\/(cn|en)\/JCInsurance\/?$/,
  /^\/(cn|en)\/marketing\/credit\/?$/,
  /^\/(cn|en)\/finance-jcpay\/?$/,
  /^\/(cn|en)\/finance-usd\/?$/,
  /^\/(cn|en)\/vip\/?$/,
  /^\/(cn|en)\/JCVerified\/?$/,
  /^\/(cn|en)\/client\/?$/,
  /^\/(cn|en)\/jctransnews\/?$/,
  /^\/(cn|en)\/news\/?$/,
  /^\/(cn|en)\/aboutus\/?$/,
  /^\/(cn|en)\/sem\/?$/,
] as const

export const AUTH_401_CLEAR_ONLY_PATH_PATTERNS = [
  /^\/(cn|en)\/membership\/tools\/risk\/blackList\/?$/,
] as const

const USER_COMP_PERMS_PATHS = [
  '/auth/userCompPerms',
  '/auth/pPerms',
] as const

/** 鉴权 / 写操作：清态后不重试，避免无意义重打或空身份提交 */
const NON_RETRYABLE_401_URL_PARTS = [
  '/auth/',
  '/logout',
  '/credit/fr/client/leadCollection',
] as const

export function is401WhitelistRoute(route: { name?: string | symbol | null }) {
  const routeName = route.name as string
  return (AUTH_401_WHITELIST_ROUTE_NAMES as readonly string[]).includes(
    routeName,
  )
}

export function is401WhitelistPath(path?: string | null) {
  if (!path) return false
  const pure = path.split('?')[0] || ''
  return AUTH_401_WHITELIST_PATH_PATTERNS.some((re) => re.test(pure))
}

function isClearOnlyRoute(routeName?: string | symbol | null) {
  if (routeName == null) return false
  return (AUTH_401_CLEAR_ONLY_ROUTE_NAMES as readonly string[]).includes(
    routeName as string,
  )
}

function isClearOnlyPath(path?: string | null) {
  if (!path) return false
  const pure = path.split('?')[0] || ''
  return AUTH_401_CLEAR_ONLY_PATH_PATTERNS.some((re) => re.test(pure))
}

function isUserCompPermsRequest(requestUrl?: string) {
  if (typeof requestUrl !== 'string') return false
  return USER_COMP_PERMS_PATHS.some((path) => requestUrl.includes(path))
}

export function isNonRetryable401Request(requestUrl?: string) {
  if (typeof requestUrl !== 'string') return false
  return NON_RETRYABLE_401_URL_PARTS.some((part) => requestUrl.includes(part))
}

type SkipLoginOptions = {
  currentRoute?: { name?: string | symbol | null; path?: string }
  navigationTargetName?: string | symbol | null
  navigationTargetPath?: string | null
  navigationFromName?: string | symbol | null
  targetNeedToken?: boolean
  requestUrl?: string
}

/** 白名单内：该路由 401 只清态、不 guest 重试 */
export function shouldClearOnlyAfter401(options: SkipLoginOptions) {
  return (
    isClearOnlyRoute(options.currentRoute?.name) ||
    isClearOnlyRoute(options.navigationTargetName) ||
    isClearOnlyPath(options.navigationTargetPath) ||
    isClearOnlyPath(options.currentRoute?.path)
  )
}

/** 当前页或导航目标页在白名单内时，401 不跳登录；
 *  userCompPerms 且 from 为白名单、目标页不需要登录时，同样不跳登录 */
export function shouldSkip401LoginRedirect(options: SkipLoginOptions) {
  if (is401WhitelistRoute(options.currentRoute ?? {})) {
    return true
  }
  if (
    options.navigationTargetName != null &&
    is401WhitelistRoute({ name: options.navigationTargetName })
  ) {
    return true
  }
  // 中间件竞态兜底：path
  if (
    is401WhitelistPath(options.navigationTargetPath) ||
    is401WhitelistPath(options.currentRoute?.path)
  ) {
    return true
  }

  if (
    isUserCompPermsRequest(options.requestUrl) &&
    is401WhitelistRoute({ name: options.navigationFromName }) &&
    !options.targetNeedToken
  ) {
    return true
  }

  return false
}

/** 白名单 401：是否允许清态后以游客身份重试 1 次 */
export function shouldRetryAsGuestAfter401Clear(
  options: SkipLoginOptions & { alreadyRetried?: boolean },
) {
  if (options.alreadyRetried) return false
  if (!shouldSkip401LoginRedirect(options)) return false

  if (shouldClearOnlyAfter401(options)) {
    return false
  }

  if (isNonRetryable401Request(options.requestUrl)) return false

  return true
}
